/**
 * Created by rathompson on 9/20/21
 * File: ViewIdentityModal
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useState } from 'react'
import {
  Bold,
  Body,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader, LoadingIcon,
} from '@panwds/react-ui'
import StringTemplate from '../../../../common/utils/StringTemplate'
import {
  BUTTON_CANCEL,
  LABEL_IDENTITY_ADDRESS,
  LABEL_DISPLAY_NAME,
  LABEL_IDENTITY_TYPE,
  LABEL_VIEW_IDENTITY,
  BUTTON_REMOVE,
  LABEL_TYPE_USER,
  ERROR_SERVER_REMOVE
} from '../../../constants/LabelConstants'
import {
  DATA_TEST_VIEW_IDENTITY_ADDRESS,
  DATA_TEST_VIEW_IDENTITY_NAME,
  DATA_TEST_VIEW_IDENTITY_TYPE,
  DATA_TEST_VIEW_IDENTITY_CANCEL,
  DATA_TEST_VIEW_IDENTITY_REMOVE
} from '../../../../common/constants/TestingDataIds'

/***
 *
 * @param closeModal callback when cancel/close button is clicked
 * @param isOpen boolean indicating whether modal is visible or not
 * @param tenant currently selected tenant
 * @param getData callback to refresh identity table
 * @param removeIdentity callback for delete identity service
 * @param id selected identity id
 * @returns {JSX.Element}
 * @constructor
 * @desc View metadata about a single identity and can also remove it
 */
const ViewIdentityModal = ({ closeModal, isOpen, tenant, getData, removeIdentity, ...rest }) => {

  /**
   *     ========= Local State ===========
   */
  const [isLoading, setIsLoading] = useState(false)
  const title = StringTemplate(LABEL_VIEW_IDENTITY, [tenant?.display_name])
  const [error, setError] = useState('')
  const [response, setResponse] = useState(null)

  /**
   *     ========= Lifecycle =============
   */
  useEffect(() => {
    if (response) {
      if (response.status === 200 || response.status === 201) {
        closeModal()
        getData()
      }
      else {
        setError(ERROR_SERVER_REMOVE)
      }
    }
  }, [closeModal, getData, response])

  /**
   *     ========= Callback =============
   */
  const onRemove = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const res = await removeIdentity(tenant?.id, [rest])
      setResponse(res)
    }
    catch (error) {
      setError(error.message || ERROR_SERVER_REMOVE)
    }
    setIsLoading(false)
  }, [removeIdentity, rest, tenant?.id])

  return (
    <Modal style={{ top: '30%' }} onClose={closeModal} isOpen={isOpen}>
      <div className='header-wrapper'>
        <ModalHeader {...{ title }}/>
      </div>
      {isLoading ? (
        <ModalBody addClassName='tw-mr-4 tw-flex tw-flex-row tw-justify-center tw-items-center'>
          <LoadingIcon size="5xl"/>
        </ModalBody>
      ) : (
        <ModalBody addClassName='tw-truncate'>
          {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
          <div className='tw-mt-2 tw-flex tw-flex-row tw-gap-1 tw-overflow-hidden'>
            <Bold>{LABEL_IDENTITY_TYPE}:</Bold>
            <Body data-testid={DATA_TEST_VIEW_IDENTITY_TYPE} size='lg' truncate>{LABEL_TYPE_USER}</Body>
          </div>
          <div className='tw-mt-2 tw-flex tw-flex-row tw-gap-1 tw-overflow-hidden'>
            <Bold>{LABEL_IDENTITY_ADDRESS}:</Bold>
            <Body data-testid={DATA_TEST_VIEW_IDENTITY_ADDRESS} size='lg' truncate>{rest?.identity_email}</Body>
          </div>
          <div className='tw-mt-2 tw-flex tw-flex-row tw-gap-1 tw-overflow-hidden'>
            <Bold>{LABEL_DISPLAY_NAME}:</Bold>
            <Body data-testid={DATA_TEST_VIEW_IDENTITY_NAME} size='lg' truncate>{rest?.display_name || rest?.identity_email}</Body>
          </div>
        </ModalBody>
      )}
      <ModalFooter>
        <Button
          data-testid={DATA_TEST_VIEW_IDENTITY_CANCEL}
          onClick={closeModal}
          appearance="secondary"
          disabled={isLoading}
        >{BUTTON_CANCEL}</Button>
        <ModalCloseButton
          data-testid={DATA_TEST_VIEW_IDENTITY_REMOVE}
          appearance='secondary-destructive'
          onClick={onRemove.bind(this)}
          disabled={isLoading}
        >{BUTTON_REMOVE}</ModalCloseButton>
      </ModalFooter>
    </Modal>
  )
}

export default ViewIdentityModal

